// Fiche d'une demande sociale (lot L7, M20) : bénéficiaire et situation (confidentiels), critères de la version
// du dépôt et proposition explicable, doublons avec leurs indices, notes internes, pièces, décision motivée,
// engagement de dépense référencé, suivi, historique. Les actes proposés suivent les droits ; un acte impossible
// est expliqué ; le serveur refait tous les contrôles.
import {
  CONSTATS_CRITERE,
  EXAMENS_DOUBLON,
  NATURES_SUIVI_SOCIAL,
  PROPOSITIONS_ELIGIBILITE,
} from '@cc/contrats';
import { Bandeau, Bouton, Lien, PuceEtat } from '@cc/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { nomCompte, peut } from '../../session/permissions';
import { dateCourte } from '../urbanisme/PageDemandes';
import { telechargerPiece, transmettre, useDemande, INVALIDER_SOCIAL } from './api';
import {
  FormulaireDecision,
  FormulaireDoublon,
  FormulaireEngagement,
  FormulaireEvaluation,
  FormulairePiece,
  FormulaireSuivi,
  FormulaireTexte,
} from './FormulairesInstruction';
import {
  libelleEtat,
  montant,
  motifDecisionImpossible,
  motifTransmissionImpossible,
  TON_ETAT_DEMANDE,
} from './logique';
import { useAction } from '../../commun/useAction';
import styles from '../achats/achats.module.css';

type Dialogue =
  | 'evaluation'
  | 'note'
  | 'classement'
  | 'message'
  | 'decision'
  | 'engagement'
  | 'suivi'
  | 'piece'
  | { doublon: string }
  | null;
const heure = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Africa/Libreville',
  });

export function PageDemandeSociale() {
  const { id } = useParams();
  const { contexte } = useSession();
  const demande = useDemande(id);
  const [d, setD] = useState<Dialogue>(null);
  const droits = {
    modifier: peut(contexte, 'social', 'modifier'),
    transmettre: peut(contexte, 'social', 'transmettre'),
    decider: peut(contexte, 'social', 'decider'),
  };
  const x = demande.data;
  const transmission = useAction({
    executer: () => transmettre(id as string),
    succes: 'Demande transmise pour décision.',
    invalider: INVALIDER_SOCIAL,
  });
  const ouverte = x ? ['recue', 'en_instruction', 'a_decider'].includes(x.etat) : false;
  const enInstruction = x ? ['recue', 'en_instruction'].includes(x.etat) : false;
  const motifTransmission = x ? motifTransmissionImpossible(x, droits) : null;
  const motifDecision = x ? motifDecisionImpossible(x, droits, nomCompte(contexte)) : null;
  return (
    <>
      <EnTetePage
        etiquette="Actions sociales"
        titre={x ? `${x.numero} : ${x.programme.intitule}` : "Demande d'aide sociale"}
        description={
          x
            ? `${x.beneficiaire}, reçue le ${dateCourte(x.recue_le)}. Données confidentielles : consultation journalisée.`
            : undefined
        }
        actions={<Lien href="/social">Toutes les demandes</Lien>}
      />
      <EtatRequete
        chargement={demande.isPending}
        erreur={demande.error}
        onReessayer={() => void demande.refetch()}
        lot="L7"
        fonction="La fiche de la demande"
      >
        {x ? (
          <>
            <div className={styles.actes}>
              <PuceEtat ton={TON_ETAT_DEMANDE[x.etat]} libelle={libelleEtat(x.etat)} />
              {enInstruction && droits.modifier ? (
                <Bouton variante="secondaire" onClick={() => setD('evaluation')}>
                  Évaluer les critères
                </Bouton>
              ) : null}
              {enInstruction && !motifTransmission ? (
                <Bouton
                  variante="principal"
                  chargement={transmission.enCours}
                  onClick={() => transmission.lancer(undefined)}
                >
                  Transmettre pour décision
                </Bouton>
              ) : null}
              {x.etat === 'a_decider' && !motifDecision ? (
                <Bouton variante="principal" onClick={() => setD('decision')}>
                  Décider
                </Bouton>
              ) : null}
              {ouverte && droits.modifier ? (
                <Bouton variante="secondaire" onClick={() => setD('note')}>
                  Note interne
                </Bouton>
              ) : null}
              {ouverte && droits.modifier ? (
                <Bouton variante="secondaire" onClick={() => setD('message')}>
                  Message à l&apos;usager
                </Bouton>
              ) : null}
              {droits.modifier ? (
                <Bouton variante="secondaire" onClick={() => setD('piece')}>
                  Déposer une pièce
                </Bouton>
              ) : null}
              {ouverte && droits.modifier ? (
                <Bouton variante="secondaire" onClick={() => setD('classement')}>
                  Classer sans suite
                </Bouton>
              ) : null}
              {x.etat === 'accordee' && droits.modifier ? (
                <Bouton variante="secondaire" onClick={() => setD('suivi')}>
                  Ajouter un suivi
                </Bouton>
              ) : null}
              {x.decision?.sens === 'accord' &&
              x.decision.montant_accorde !== null &&
              !x.decision.engagement &&
              droits.modifier ? (
                <Bouton variante="secondaire" onClick={() => setD('engagement')}>
                  Référencer l&apos;engagement
                </Bouton>
              ) : null}
            </div>
            {enInstruction && motifTransmission ? (
              <Bandeau type="info">{`Transmission pour décision : ${motifTransmission}`}</Bandeau>
            ) : null}
            {motifDecision ? <Bandeau type="info">{`Décision : ${motifDecision}`}</Bandeau> : null}
            {transmission.messageErreur ? (
              <Bandeau type="erreur">{transmission.messageErreur}</Bandeau>
            ) : null}
            <section className={styles.bloc} aria-labelledby="titre-beneficiaire">
              <h2 id="titre-beneficiaire">Bénéficiaire et situation</h2>
              <dl className={styles.fiche}>
                <dt>Nom</dt>
                <dd>
                  {[x.beneficiaire_detail.prenoms, x.beneficiaire_detail.nom]
                    .filter(Boolean)
                    .join(' ')}
                </dd>
                <dt>Date de naissance</dt>
                <dd>{x.beneficiaire_detail.date_naissance ?? 'non renseignée'}</dd>
                <dt>Téléphone</dt>
                <dd>{x.beneficiaire_detail.telephone ?? 'non renseigné'}</dd>
                <dt>Quartier</dt>
                <dd>{x.beneficiaire_detail.quartier ?? 'non renseigné'}</dd>
                <dt>Compte du portail</dt>
                <dd>
                  {x.beneficiaire_detail.usager_lie
                    ? "Lié : le demandeur voit l'état et le message qui lui est destiné"
                    : 'Aucun'}
                </dd>
                <dt>Situation exposée</dt>
                <dd>{x.situation}</dd>
                <dt>Montant demandé</dt>
                <dd>{montant(x.montant_demande, x.fictif)}</dd>
                <dt>Message destiné à l&apos;usager</dt>
                <dd>{x.message_usager ?? 'aucun'}</dd>
              </dl>
            </section>
            <section className={styles.bloc} aria-labelledby="titre-criteres">
              <h2 id="titre-criteres">{`Critères (version ${x.criteres.version}) et proposition`}</h2>
              <ul className={styles.liste}>
                {x.criteres.criteres.map((c) => {
                  const e = x.evaluations.find((v) => v.code === c.code);
                  return (
                    <li
                      key={c.code}
                    >{`${c.libelle}${c.obligatoire ? '' : ' (facultatif)'} : ${e ? CONSTATS_CRITERE[e.constat] : 'non évalué'}`}</li>
                  );
                })}
              </ul>
              <Bandeau type={x.proposition.resultat === 'eligible_propose' ? 'info' : 'attention'}>
                <strong>{PROPOSITIONS_ELIGIBILITE[x.proposition.resultat]}</strong>
                <ul className={styles.detail}>
                  {x.proposition.explications.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </Bandeau>
            </section>
            <section className={styles.bloc} aria-labelledby="titre-doublons">
              <h2 id="titre-doublons">Doublons potentiels</h2>
              {x.doublons.length === 0 ? (
                <p className={styles.detail}>Aucun rapprochement dans ce programme.</p>
              ) : (
                <ul className={styles.liste}>
                  {x.doublons.map((b) => (
                    <li key={b.id}>
                      <Lien href={`/social/demandes/${b.demande.id}`} className="cc-mono">
                        {b.demande.numero}
                      </Lien>
                      {` (${libelleEtat(b.demande.etat)}) : ${b.indices.map((i) => i.libelle).join(' ; ')}. ${EXAMENS_DOUBLON[b.examen]}${b.motif ? ` : ${b.motif}` : ''}`}
                      {b.examen === 'a_examiner' && ouverte && droits.modifier ? (
                        <>
                          {' '}
                          <Bouton variante="secondaire" onClick={() => setD({ doublon: b.id })}>
                            Examiner
                          </Bouton>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-decision">
              <h2 id="titre-decision">Décision</h2>
              {x.decision ? (
                <dl className={styles.fiche}>
                  <dt>Sens</dt>
                  <dd>{x.decision.sens === 'accord' ? 'Accord' : 'Refus'}</dd>
                  <dt>Motif</dt>
                  <dd>{x.decision.motif}</dd>
                  <dt>Nature de l&apos;aide</dt>
                  <dd>{x.decision.nature_aide ?? 'sans objet'}</dd>
                  <dt>Montant accordé</dt>
                  <dd>{montant(x.decision.montant_accorde, x.fictif)}</dd>
                  <dt>Critères appliqués</dt>
                  <dd>{`Version ${x.decision.criteres_version} ; proposition au moment : ${PROPOSITIONS_ELIGIBILITE[x.decision.proposition_au_moment]}`}</dd>
                  <dt>Décidée par</dt>
                  <dd>{`${x.decision.decide_par}, ${heure(x.decision.decide_le)}`}</dd>
                  <dt>Engagement de dépense</dt>
                  <dd>
                    {x.decision.engagement
                      ? `${x.decision.engagement.numero ?? 'sans numéro'} (${x.decision.engagement.etat}, ${montant(x.decision.engagement.montant)})`
                      : x.decision.montant_accorde !== null
                        ? 'à référencer (aucun paiement par ce module)'
                        : 'sans objet'}
                  </dd>
                </dl>
              ) : (
                <p className={styles.detail}>
                  {x.etat === 'a_decider'
                    ? 'En attente de décision par une personne habilitée distincte de celle qui a transmis.'
                    : 'Pas encore transmise pour décision.'}
                </p>
              )}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-suivi">
              <h2 id="titre-suivi">Suivi</h2>
              {x.suivis.length === 0 ? (
                <p className={styles.detail}>Aucun suivi.</p>
              ) : (
                <ul className={styles.liste}>
                  {x.suivis.map((s) => (
                    <li
                      key={s.id}
                    >{`${s.date_suivi} · ${NATURES_SUIVI_SOCIAL[s.nature]} : ${s.observation} (${s.auteur})`}</li>
                  ))}
                </ul>
              )}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-notes">
              <h2 id="titre-notes">Notes internes</h2>
              {x.notes.length === 0 ? (
                <p className={styles.detail}>Aucune note.</p>
              ) : (
                <ul className={styles.liste}>
                  {x.notes.map((n) => (
                    <li key={n.id}>{`${heure(n.cree_le)} · ${n.auteur} : ${n.contenu}`}</li>
                  ))}
                </ul>
              )}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-pieces">
              <h2 id="titre-pieces">Pièces</h2>
              {x.pieces.length === 0 ? (
                <p className={styles.detail}>Aucune pièce.</p>
              ) : (
                <ul className={styles.liste}>
                  {x.pieces.map((p) => (
                    <li key={p.id}>
                      {`${p.type_piece} · ${p.type_mime} · ${heure(p.deposee_le)} `}
                      <Bouton
                        variante="secondaire"
                        onClick={() => void telechargerPiece(x.id, p.id, `piece-${p.type_piece}`)}
                      >
                        Télécharger
                      </Bouton>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-historique">
              <h2 id="titre-historique">Historique</h2>
              <ol className={styles.liste}>
                {x.historique.map((h, i) => (
                  <li key={`${h.horodatage}-${i}`}>
                    <span className="cc-mono">{heure(h.horodatage)}</span> {h.libelle}
                    {h.auteur ? `, par ${h.auteur}` : ''}
                  </li>
                ))}
              </ol>
            </section>
            {d === 'evaluation' ? (
              <FormulaireEvaluation demande={x} onFermer={() => setD(null)} />
            ) : null}
            {d === 'note' || d === 'classement' || d === 'message' ? (
              <FormulaireTexte demande={x} mode={d} onFermer={() => setD(null)} />
            ) : null}
            {d === 'decision' ? (
              <FormulaireDecision demande={x} onFermer={() => setD(null)} />
            ) : null}
            {d === 'engagement' ? (
              <FormulaireEngagement demande={x} onFermer={() => setD(null)} />
            ) : null}
            {d === 'suivi' ? <FormulaireSuivi demande={x} onFermer={() => setD(null)} /> : null}
            {d === 'piece' ? <FormulairePiece demande={x} onFermer={() => setD(null)} /> : null}
            {d && typeof d === 'object' ? (
              <FormulaireDoublon demande={x} doublonId={d.doublon} onFermer={() => setD(null)} />
            ) : null}
          </>
        ) : null}
      </EtatRequete>
    </>
  );
}
